import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ScrollText, Loader2, ExternalLink } from "lucide-react";

const STORAGE_KEY = "merchant_rules_ack_seen";

// Two-step "Become a Merchant" gate: (1) the ground-rules notice, skipped on repeat visits if
// the Knight opted to not see it again, then (2) the seller records form Call to Arms keeps on
// file (Stripe Connect separately handles legal/banking KYC). Submitting sends them to Stripe.
export function MerchantOnboardingModal({ open, onOpenChange, userId, defaultEmail, onContinueToStripe }) {
  const [step, setStep] = useState("rules");
  const [dontShowAgain, setDontShowAgain] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [realName, setRealName] = useState("");
  const [contactEmail, setContactEmail] = useState(defaultEmail || "");
  const [bio, setBio] = useState("");
  const [agreedRules, setAgreedRules] = useState(false);
  const [agreedAdult, setAgreedAdult] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const seen = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    setStep(seen.includes(userId) ? "profile" : "rules");
  }, [open, userId]);

  const acceptRules = () => {
    if (dontShowAgain) {
      const seen = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      if (!seen.includes(userId)) localStorage.setItem(STORAGE_KEY, JSON.stringify([...seen, userId]));
    }
    setStep("profile");
  };

  const submitProfile = async () => {
    if (!displayName.trim() || !realName.trim() || !contactEmail.trim()) return toast.error("Fill out every field");
    if (!agreedRules) return toast.error("You need to agree to the ground rules to continue");
    setSubmitting(true);
    try {
      await api.post("/bazaar/merchant/profile", { display_name: displayName, real_name: realName, contact_email: contactEmail, bio, agreed_rules: agreedRules, agreed_adult_marking: agreedAdult });
      onOpenChange(false);
      onContinueToStripe();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save your profile");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#322216] border-white/10 text-[#f1e0c5]" data-testid="merchant-onboarding-modal">
        {step === "rules" ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <ScrollText className="w-5 h-5 text-[#D4AF37]" /> Welcome, Merchant!
              </DialogTitle>
              <DialogDescription className="sr-only">Bazaar seller ground rules</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-[#f1e0c5]/70 leading-relaxed">
              Welcome! Thank you for deciding to sell in our bazaar. A few ground rules, first is
              simple, don't scam people, give them what they pay for. Second, Call to Arms doesn't
              charge you fees, we never intend to charge you fees, if at any point a buyer reports
              you for any fees related to us, your account will be suspended until you fix your
              prices, and on a second offense your account will be deleted. Third rule, you may
              sell explicit content, however you need to mark that product as adult and any
              pictures on the bazaar are censored. Final rule, have fun, do what you love, bring
              joy. God Bless :)
            </p>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox data-testid="merchant-rules-dont-show-checkbox" checked={dontShowAgain} onCheckedChange={setDontShowAgain} />
              Don't show this again
            </label>
            <Button data-testid="merchant-rules-agree-button" onClick={acceptRules} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-xs">
              I Agree
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Seller Records</DialogTitle>
              <DialogDescription className="text-xs text-[#f1e0c5]/50">We keep a light record on file for every seller - Stripe handles the rest.</DialogDescription>
            </DialogHeader>
            <Input data-testid="merchant-profile-display-name-input" placeholder="Display name (shown on the Bazaar)" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="bg-black/30 border-white/10" />
            <Input data-testid="merchant-profile-real-name-input" placeholder="Real name" value={realName} onChange={(e) => setRealName(e.target.value)} className="bg-black/30 border-white/10" />
            <Input data-testid="merchant-profile-email-input" type="email" placeholder="Contact email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="bg-black/30 border-white/10" />
            <Textarea data-testid="merchant-profile-bio-input" placeholder="Short bio - what do you sell?" value={bio} onChange={(e) => setBio(e.target.value)} className="bg-black/30 border-white/10" rows={2} maxLength={500} />
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <Checkbox data-testid="merchant-profile-agree-rules-checkbox" checked={agreedRules} onCheckedChange={setAgreedRules} className="mt-0.5" />
              <span>
                I have read and agree to the{" "}
                <a href="/legal/seller-terms" target="_blank" rel="noopener noreferrer" data-testid="merchant-profile-terms-link" className="text-[#D4AF37] underline inline-flex items-center gap-1">
                  Bazaar Seller Terms & Conditions <ExternalLink className="w-3 h-3" />
                </a>
              </span>
            </label>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <Checkbox data-testid="merchant-profile-agree-adult-checkbox" checked={agreedAdult} onCheckedChange={setAgreedAdult} className="mt-0.5" />
              I will correctly mark any adult-content items I list
            </label>
            <Button data-testid="merchant-profile-submit-button" onClick={submitProfile} disabled={submitting} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-xs gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Next: Continue to Stripe to Finish Setup
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
